import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Empty, Form, Input, InputNumber, Modal, Segmented, Select, Skeleton,
  Space, Tag, Tooltip, Typography } from 'antd';
import type {
  CreateServiceTemplateDraftRequest,
  ServiceTemplateStatus,
  ServiceTemplateVersionDetail,
  ServiceTemplateVersionSummary,
  UpdateServiceTemplateDraftRequest,
} from '@youkuai/contracts';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import { ApiRequestError } from './project-api.ts';
import { cloneServiceTemplate, createServiceTemplate, getServiceTemplate, listServiceTemplates,
  previewServiceTemplatePublish, publishServiceTemplate, updateServiceTemplate } from './service-template-api.ts';
import { DateInput } from './date-input.tsx';

const { Title } = Typography;
const publication = { taskKey: 'PUBLICATION', name: '发布', description: '将内容发布至约定平台并登记成功结果',
  unit: '条', quantity: 1, required: true, rate: '0.00' } as const;
type FormTask = { taskKey: string; name: string; description: string; unit: string; quantity: number;
  required: boolean; rate: string };
type FormVariant = { variantKey: string; name: string; description: string; tasks: FormTask[] };
type TemplateForm = { familyName: string; contentType: 'VIDEO' | 'ARTICLE'; versionName: string;
  deliverable: string; defaultCapacity: number; effectiveOn: string; variants: FormVariant[] };

export function nextTemplateKey(existing: string[], prefix: 'VARIANT' | 'TASK'): string {
  const used = new Set(existing.map((value) => value.toUpperCase()));
  let sequence = 1;
  while (used.has(`${prefix}-${String(sequence).padStart(2, '0')}`)) sequence += 1;
  return `${prefix}-${String(sequence).padStart(2, '0')}`;
}

/** 模板任务必须使用标准语义编码：内容记录表、结算费用分类和内容详情都按这些编码识别任务。 */
export const CANONICAL_TASK_TYPES: Record<'VIDEO' | 'ARTICLE', ReadonlyArray<{ value: string; label: string }>> = {
  VIDEO: [
    { value: 'PROJECT_MANAGEMENT', label: '项目管理' },
    { value: 'VIDEO_PLANNING', label: '视频策划' },
    { value: 'VIDEO_EDITING', label: '视频剪辑' },
  ],
  ARTICLE: [
    { value: 'PROJECT_MANAGEMENT', label: '项目管理' },
    { value: 'ARTICLE_WRITING', label: '文章文案' },
    { value: 'ARTICLE_DESIGN', label: '文章设计' },
  ],
};
const CROSS_TYPE_TASK_KEY: Record<string, string> = {
  VIDEO_PLANNING: 'ARTICLE_WRITING', ARTICLE_WRITING: 'VIDEO_PLANNING',
  VIDEO_EDITING: 'ARTICLE_DESIGN', ARTICLE_DESIGN: 'VIDEO_EDITING',
};
export function taskTypeOptions(contentType: 'VIDEO' | 'ARTICLE', used: string[], current: string) {
  const currentKey = current.toUpperCase();
  return CANONICAL_TASK_TYPES[contentType].map((option) => ({
    ...option, disabled: option.value !== currentKey && used.map((key) => key.toUpperCase()).includes(option.value),
  }));
}
export function nextCanonicalTaskKey(contentType: 'VIDEO' | 'ARTICLE', used: string[]): string | null {
  const taken = new Set(used.map((key) => key.toUpperCase()));
  return CANONICAL_TASK_TYPES[contentType].find((option) => !taken.has(option.value))?.value ?? null;
}
/** 内容类型切换时把另一种内容类型的标准编码换成当前类型的对应编码，避免留下不可保存的旧编码。 */
export function retargetVariantTaskKeys(variants: FormVariant[], contentType: 'VIDEO' | 'ARTICLE'): FormVariant[] {
  const allowed = new Set(CANONICAL_TASK_TYPES[contentType].map((option) => option.value));
  return variants.map((variant) => {
    const used: string[] = variant.tasks.filter((task) => task.taskKey === 'PUBLICATION').map((task) => task.taskKey);
    const tasks = variant.tasks.map((task) => {
      if (task.taskKey === 'PUBLICATION') return task;
      let nextKey = task.taskKey;
      if (!allowed.has(nextKey)) {
        const mapped = CROSS_TYPE_TASK_KEY[nextKey];
        nextKey = mapped && allowed.has(mapped) && !used.includes(mapped) ? mapped
          : nextCanonicalTaskKey(contentType, used) ?? nextKey;
      }
      used.push(nextKey);
      return { ...task, taskKey: nextKey };
    });
    return { ...variant, tasks };
  });
}

export function composeServiceTemplateDraft(values: TemplateForm, editing: ServiceTemplateVersionDetail | null):
CreateServiceTemplateDraftRequest | UpdateServiceTemplateDraftRequest {
  const fields = { versionName: values.versionName, deliverable: values.deliverable,
    defaultCapacity: values.defaultCapacity, effectiveOn: values.effectiveOn,
    variants: values.variants.map((variant) => ({ variantKey: variant.variantKey,
      name: variant.name, description: variant.description,
      tasks: variant.tasks.map(({ taskKey, name, description, unit, quantity, required, rate }) =>
        ({ taskKey, name, description: description.trim() || name.trim(), unit, quantity, required, rate })) })) };
  return editing ? { ...fields, expectedVersion: editing.version } : { ...fields, expectedVersion: 0,
    familyName: values.familyName, contentType: values.contentType };
}

function defaultVariant(index = 1, contentType: 'VIDEO' | 'ARTICLE' = 'VIDEO'): FormVariant {
  return { variantKey: `VARIANT-${String(index).padStart(2, '0')}`, name: '', description: '', tasks: [
    { taskKey: nextCanonicalTaskKey(contentType, []) ?? 'TASK-01', name: '', description: '', unit: '次',
      quantity: 1, required: true, rate: '0.00' },
    { ...publication },
  ] };
}

function statusLabel(status: ServiceTemplateStatus): string {
  return status === 'DRAFT' ? '草稿' : status === 'PUBLISHED' ? '当前生效' : '历史版本';
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'VERSION_CONFLICT') return '版本已被其他人更新，请刷新后再编辑。';
    if (error.code === 'DRAFT_EXISTS') return '这个模板系列已有一份草稿，请先处理现有草稿。';
    if (error.code === 'ARCHIVED') return '该模板族已归档，只能查看历史，不能编辑或发布。';
    if (error.code === 'PUBLICATION_TASK_LOCKED') return '固定“发布”项的名称、说明、单位、数量和必需属性不可修改。';
    if (error.code === 'TEMPLATE_INCOMPLETE') return '每个规格至少需要一个生产任务，且必须保留固定“发布”项。';
    if (error.code === 'EFFECTIVE_DATE_CONFLICT') return '同一模板系列已有相同生效日期的历史版本。';
    if (error.code === 'FORBIDDEN') return '当前角色没有执行此操作的权限。';
    if (error.code === 'SERVICE_TEMPLATE_COMMAND_INVALID') return '表单内容不符合要求，请检查每个规格、任务字段后重试。';
    if (error.status >= 500) return '服务暂时不可用；再次提交会复用同一操作，不会重复创建。';
    return `操作失败（${error.code}）`;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

function money(value: string): string {
  return `¥${Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ServiceTemplatesPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const session = state.kind === 'ready' ? state.session : null;
  const accountId = session?.userId ?? '';
  const canRead = session?.capabilities.includes('template.read') ?? false;
  const canManage = session?.capabilities.includes('template.manage') ?? false;
  const canPublish = session?.capabilities.includes('template.publish') ?? false;
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ServiceTemplateStatus | undefined>();
  const [archivedView, setArchivedView] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<ServiceTemplateVersionDetail | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<ServiceTemplateVersionDetail | null>(null);
  const [cloneDate, setCloneDate] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form] = Form.useForm<TemplateForm>();
  const saveAttempt = useRef<{ payload: string; key: string } | null>(null);
  const cloneAttempt = useRef<{ payload: string; key: string } | null>(null);
  const publishAttempt = useRef<{ payload: string; key: string } | null>(null);
  const client = useQueryClient();

  const templates = useInfiniteQuery({
    queryKey: ['service-templates', accountId, search, status ?? 'ALL', archivedView ? 'ARCHIVED' : 'ACTIVE'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listServiceTemplates(token, { ...(search ? { search } : {}),
      ...(status ? { status } : {}), archived: archivedView, ...(pageParam ? { afterId: pageParam } : {}) }),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: canRead && !!token,
  });
  const rows = templates.data?.pages.flatMap((page) => page.items) ?? [];
  const detail = useQuery({ queryKey: ['service-template', accountId, selectedId],
    queryFn: () => getServiceTemplate(token, selectedId!), enabled: canRead && !!selectedId });
  const preview = useQuery({ queryKey: ['service-template-publish-preview', accountId, detail.data?.versionId,
    detail.data?.version], queryFn: () => previewServiceTemplatePublish(token, detail.data!.versionId),
    enabled: publishOpen && canPublish && detail.data?.status === 'DRAFT' });
  const createMutation = useMutation({ mutationFn: ({ input, key }: { input: CreateServiceTemplateDraftRequest; key: string }) =>
    createServiceTemplate(token, input, key) });
  const updateMutation = useMutation({ mutationFn: ({ versionId, input, key }:
    { versionId: string; input: UpdateServiceTemplateDraftRequest; key: string }) =>
    updateServiceTemplate(token, versionId, input, key) });
  const cloneMutation = useMutation({ mutationFn: ({ source, effectiveOn, key }:
    { source: ServiceTemplateVersionDetail; effectiveOn: string; key: string }) =>
    cloneServiceTemplate(token, source.versionId, { expectedVersion: source.version, effectiveOn }, key) });
  const publishMutation = useMutation({ mutationFn: ({ row, key }: { row: ServiceTemplateVersionDetail; key: string }) =>
    publishServiceTemplate(token, row.versionId, row.version, key) });

  useEffect(() => {
    if (!selectedId && rows[0]) setSelectedId(rows[0].versionId);
    if (selectedId && rows.length && !rows.some((row) => row.versionId === selectedId)) setSelectedId(rows[0]!.versionId);
  }, [rows, selectedId]);

  useEffect(() => {
    setSelectedId(null); setFormOpen(false); setEditing(null); setPublishOpen(false); setCloneSource(null);
  }, [accountId]);

  function openCreate() {
    setEditing(null); setNotice(null); saveAttempt.current = null; setFormOpen(true);
    form.setFieldsValue({ familyName: '', contentType: 'VIDEO', versionName: '标准版', deliverable: '',
      defaultCapacity: 30, effectiveOn: new Date().toISOString().slice(0, 10), variants: [defaultVariant(1, 'VIDEO')] });
  }

  function openEdit(value: ServiceTemplateVersionDetail) {
    setEditing(value); setNotice(null); saveAttempt.current = null; setFormOpen(true);
    form.setFieldsValue({ familyName: value.familyName, contentType: value.contentType,
      versionName: value.versionName, deliverable: value.deliverable, defaultCapacity: value.defaultCapacity,
      effectiveOn: value.effectiveOn, variants: value.variants.map((variant) => ({ variantKey: variant.variantKey,
        name: variant.name, description: variant.description, tasks: variant.tasks.map((task) => ({
          taskKey: task.taskKey, name: task.name, description: task.description, unit: task.unit,
          quantity: task.quantity, required: task.required, rate: task.rate,
        })) })) });
  }

  async function save(values: TemplateForm) {
    const input = composeServiceTemplateDraft(values, editing);
    const payload = JSON.stringify({ input, versionId: editing?.versionId ?? null });
    if (saveAttempt.current?.payload !== payload) saveAttempt.current = {
      payload, key: `service-template-save-${crypto.randomUUID()}` };
    try {
      const result = editing
        ? await updateMutation.mutateAsync({ versionId: editing.versionId,
            input: input as UpdateServiceTemplateDraftRequest, key: saveAttempt.current.key })
        : await createMutation.mutateAsync({ input: input as CreateServiceTemplateDraftRequest,
            key: saveAttempt.current.key });
      setFormOpen(false); setEditing(null); saveAttempt.current = null; setSelectedId(result.versionId);
      await client.invalidateQueries({ queryKey: ['service-templates', accountId] });
      await client.invalidateQueries({ queryKey: ['service-template', accountId, result.versionId] });
      setNotice({ type: 'success', text: `${result.familyCode} V${result.versionNo} 草稿已保存。` });
    } catch (error) { setNotice({ type: 'error', text: errorMessage(error) }); }
  }

  async function cloneVersion() {
    if (!cloneSource || !cloneDate) return;
    const payload = `${cloneSource.versionId}:${cloneSource.version}:${cloneDate}`;
    if (cloneAttempt.current?.payload !== payload) cloneAttempt.current = {
      payload, key: `service-template-clone-${crypto.randomUUID()}` };
    try {
      const result = await cloneMutation.mutateAsync({ source: cloneSource, effectiveOn: cloneDate,
        key: cloneAttempt.current.key });
      setCloneSource(null); cloneAttempt.current = null; setSelectedId(result.versionId);
      await client.invalidateQueries({ queryKey: ['service-templates', accountId] });
      setNotice({ type: 'success', text: `已复制为 V${result.versionNo} 草稿，原版本保持不变。` });
    } catch (error) { setNotice({ type: 'error', text: errorMessage(error) }); }
  }

  async function publishVersion() {
    const row = detail.data;
    if (!row) return;
    const payload = `${row.versionId}:${row.version}`;
    if (publishAttempt.current?.payload !== payload) publishAttempt.current = {
      payload, key: `service-template-publish-${crypto.randomUUID()}` };
    try {
      const result = await publishMutation.mutateAsync({ row, key: publishAttempt.current.key });
      publishAttempt.current = null; setPublishOpen(false);
      await client.invalidateQueries({ queryKey: ['service-templates', accountId] });
      await client.invalidateQueries({ queryKey: ['service-template', accountId, result.versionId] });
      setNotice({ type: 'success', text: `V${result.versionNo} 已发布；旧当前版已转为历史，既有服务包不会改变。` });
    } catch (error) { setNotice({ type: 'error', text: errorMessage(error) }); }
  }

  return <main className="page-shell customer-shell service-template-shell">
    <Link className="back-link" to="/projects">← 返回我的项目</Link>
    <div className="service-template-heading">
      <div><div className="section-kicker">FOUNDATION / SERVICE TEMPLATE &amp; RATE</div>
        <Title level={1}>服务模板与费率</Title><p>任务费用和发布预算即当前费率，在同一个不可变版本中维护。</p></div>
      {canManage && <Button type="primary" onClick={openCreate}><Icon name="plus" size={16} strokeWidth={2} /> 新建模板</Button>}
    </div>
    {!canRead ? <Alert type="error" showIcon title="当前角色没有服务模板访问权限" /> : <>
      {notice && <Alert className="workspace-alert" type={notice.type} showIcon title={notice.text} closable
        onClose={() => setNotice(null)} />}
      <div className="service-template-toolbar">
        <Input.Search value={searchText} onChange={(event) => setSearchText(event.target.value)}
          onSearch={(value) => { setSearch(value.trim()); setSelectedId(null); }} allowClear
          placeholder="搜索模板编号、名称或版本名称" />
        <Segmented value={archivedView ? 'archived' : 'active'} onChange={(value) => {
          setArchivedView(value === 'archived'); setSelectedId(null); }}
          options={[{ value: 'active', label: '标准库' }, { value: 'archived', label: '历史归档' }]} />
        <Select allowClear value={status} placeholder="全部状态" onChange={(value) => { setStatus(value); setSelectedId(null); }}
          options={[{ value: 'DRAFT', label: '草稿' }, { value: 'PUBLISHED', label: '当前生效' },
            { value: 'HISTORICAL', label: '历史版本' }]} />
        <Button loading={templates.isFetching} onClick={() => void templates.refetch()}>刷新</Button>
      </div>
      {templates.isError && <Alert type="error" showIcon title="服务模板读取失败" description={errorMessage(templates.error)} />}
      <div className="service-template-workbench">
        <aside className="service-template-list" aria-label="服务模板版本列表">
          <header><strong>{archivedView ? '历史归档' : '标准库'}</strong><span>{rows.length}</span></header>
          {templates.isPending && <Skeleton active paragraph={{ rows: 6 }} />}
          {!templates.isPending && rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={archivedView ? '没有已归档模板' : '还没有服务模板'} />}
          {rows.map((row) => <button type="button" key={row.versionId}
            className={selectedId === row.versionId ? 'is-selected' : ''} onClick={() => setSelectedId(row.versionId)}>
            <span className="service-template-list-top"><b>{row.familyCode}</b>
              <Tag variant="filled" color={row.status === 'PUBLISHED' ? 'success' : row.status === 'DRAFT' ? 'processing' : 'default'}>
                {statusLabel(row.status)}</Tag>{row.archived && <Tag variant="filled">已归档</Tag>}</span>
            <strong>{row.familyName}</strong>
            <small>V{row.versionNo} · {row.versionName}</small>
            <small>{row.variantCount} 个规格 · {row.productionTaskCount} 个生产任务</small>
          </button>)}
          {templates.hasNextPage && <Button block loading={templates.isFetchingNextPage}
            onClick={() => void templates.fetchNextPage()}>加载更多</Button>}
        </aside>
        <section className="service-template-detail">
          {!selectedId && !templates.isPending && <Empty description="选择左侧版本查看详情" />}
          {!!selectedId && detail.isPending && <Skeleton active paragraph={{ rows: 10 }} />}
          {detail.isError && <Alert type="error" title="版本详情读取失败" description={errorMessage(detail.error)} />}
          {detail.data && <TemplateDetail detail={detail.data} archived={detail.data.archived} canManage={canManage} canPublish={canPublish}
            onEdit={() => openEdit(detail.data!)} onClone={() => { setCloneSource(detail.data!);
              setCloneDate(detail.data!.effectiveOn); cloneAttempt.current = null; }}
            onPublish={() => { setPublishOpen(true); publishAttempt.current = null; }} />}
        </section>
      </div>
    </>}

    <Modal open={formOpen} width={1040} footer={null} destroyOnHidden forceRender
      title={editing ? `编辑 ${editing.familyCode} V${editing.versionNo} 草稿` : '新建服务模板'}
      onCancel={() => { setFormOpen(false); setEditing(null); saveAttempt.current = null; }}>
      <p className="service-template-modal-intro">每个规格都需要至少一个生产任务；任务类型决定内容记录、分工和结算里的归类，必须使用标准类型；固定“发布”项会自动保留，只能修改发布预算。</p>
      <Form form={form} layout="vertical" onFinish={(values) => void save(values)}
        onValuesChange={(changed: Partial<TemplateForm>) => {
          if (!editing && changed.contentType) form.setFieldsValue({
            variants: retargetVariantTaskKeys(form.getFieldValue('variants') ?? [], changed.contentType) });
        }}>
        <div className="service-template-form-grid">
          <Form.Item name="familyName" label="模板名称" rules={[{ required: true }]}>
            <Input disabled={!!editing} maxLength={120} placeholder="例如：医生短视频标准服务" /></Form.Item>
          <Form.Item name="contentType" label="内容类型" rules={[{ required: true }]}>
            <Select disabled={!!editing} options={[{ value: 'VIDEO', label: '视频' }, { value: 'ARTICLE', label: '文章' }]} />
          </Form.Item>
          <Form.Item name="versionName" label="版本名称" rules={[{ required: true }]}>
            <Input maxLength={120} placeholder="例如：2026 标准版" /></Form.Item>
          <Form.Item name="effectiveOn" label="生效日期" rules={[{ required: true }]}><DateInput /></Form.Item>
          <Form.Item name="defaultCapacity" label="参考容量" rules={[{ required: true }]}>
            <InputNumber min={1} max={100000} precision={0} /></Form.Item>
        </div>
        <Form.Item name="deliverable" label="交付内容与口径" rules={[{ required: true }]}>
          <Input.TextArea rows={3} maxLength={2000} placeholder="描述这个模板最终交付什么，以及统一验收口径" />
        </Form.Item>
        <Form.List name="variants">{(variantFields, variantActions) => <div className="service-template-editor">
          <div className="service-template-editor-title"><div><strong>动态服务规格</strong><span>不限制 A / B / C，可继续增加第四个及更多规格。</span></div>
            <Button onClick={() => { const current = form.getFieldValue('variants') ?? [];
              variantActions.add({ ...defaultVariant(current.length + 1, form.getFieldValue('contentType') ?? 'VIDEO'),
                variantKey: nextTemplateKey(current.map((item: FormVariant) => item.variantKey), 'VARIANT') }); }}><Icon name="plus" size={16} strokeWidth={2} /> 添加规格</Button></div>
          {variantFields.map((variantField, variantIndex) => <section className="service-variant-editor" key={variantField.key}>
            <header><span>规格 {String(variantIndex + 1).padStart(2, '0')}</span><Space>
              <Button size="small" disabled={variantIndex === 0} onClick={() => variantActions.move(variantIndex, variantIndex - 1)}>上移</Button>
              <Button size="small" disabled={variantIndex === variantFields.length - 1}
                onClick={() => variantActions.move(variantIndex, variantIndex + 1)}>下移</Button>
              <Button size="small" danger disabled={variantFields.length === 1}
                onClick={() => variantActions.remove(variantField.name)}>移除规格</Button></Space></header>
            <Form.Item hidden name={[variantField.name, 'variantKey']}><Input /></Form.Item>
            <div className="service-template-form-grid two">
              <Form.Item name={[variantField.name, 'name']} label="规格名称" rules={[{ required: true }]}>
                <Input maxLength={80} placeholder="例如：重点内容" /></Form.Item>
              <Form.Item name={[variantField.name, 'description']} label="规格说明" rules={[{ required: true }]}>
                <Input maxLength={500} placeholder="说明适用范围和内容要求" /></Form.Item>
            </div>
            <Form.List name={[variantField.name, 'tasks']}>{(taskFields, taskActions) => <div className="service-task-editor">
              <div className="service-task-head"><span>任务类型 / 名称</span><span>说明</span><span>单位 / 数量</span><span>费用</span><span>操作</span></div>
              {taskFields.map((taskField, taskIndex) => {
                const taskKey = form.getFieldValue(['variants', variantField.name, 'tasks', taskField.name, 'taskKey']);
                const fixed = taskKey === 'PUBLICATION';
                return <div className={`service-task-row${fixed ? ' is-fixed' : ''}`} key={taskField.key}>
                  <div className="service-task-main">
                    <Form.Item noStyle shouldUpdate>{() => {
                      const contentType = (form.getFieldValue('contentType') ?? 'VIDEO') as 'VIDEO' | 'ARTICLE';
                      const variantTasks: FormTask[] = form.getFieldValue(['variants', variantField.name, 'tasks']) ?? [];
                      const siblings = variantTasks.filter((item) => item.taskKey && item.taskKey !== taskKey)
                        .map((item) => item.taskKey);
                      const typeControl = fixed
                        ? <Select size="small" disabled options={[{ value: 'PUBLICATION', label: '发布' }]} />
                        : <Select size="small" placeholder="选择标准任务类型" aria-label="任务类型"
                            options={taskTypeOptions(contentType, siblings, taskKey)} />;
                      return <Form.Item name={[taskField.name, 'taskKey']} rules={fixed ? [{ required: true }] : [{
                        validator: (_rule, value: string) => CANONICAL_TASK_TYPES[contentType]
                          .some((option) => option.value === value)
                          ? Promise.resolve() : Promise.reject(new Error('请选择标准任务类型')) }]}>
                        {typeControl}</Form.Item>;
                    }}</Form.Item>
                    <Form.Item name={[taskField.name, 'name']} rules={[{ required: true, message: '请输入任务名称' }]}>
                      <Input disabled={fixed} maxLength={80} placeholder={fixed ? '系统锁定' : '任务名称'} /></Form.Item>
                  </div>
                  <Form.Item name={[taskField.name, 'description']}>
                    <Input disabled={fixed} maxLength={500} placeholder="留空时使用任务名称" /></Form.Item>
                  <div className="service-task-unit"><Form.Item name={[taskField.name, 'unit']} rules={[{ required: true, message: '请输入单位' }]}>
                    <Input disabled={fixed} maxLength={20} /></Form.Item><Form.Item name={[taskField.name, 'quantity']}>
                    <InputNumber disabled={fixed} min={1} max={1000} precision={0} /></Form.Item></div>
                  <Form.Item name={[taskField.name, 'rate']} rules={[{ required: true, message: '请输入任务费用' },
                    { pattern: /^(?:0|[1-9]\d{0,13})(?:\.\d{1,2})?$/, message: '最多两位小数' }]}>
                    <Input prefix="¥" inputMode="decimal" aria-label={fixed ? '发布预算' : '任务费用'} /></Form.Item>
                  <div className="service-task-actions">{fixed ? <><Form.Item hidden name={[taskField.name, 'required']}><Input /></Form.Item>
                    <Tooltip title="名称、说明、单位、数量和必需属性由系统锁定">
                    <Tag variant="filled">固定发布项</Tag></Tooltip></> : <><Form.Item name={[taskField.name, 'required']}
                      valuePropName="checked" noStyle><Checkbox>必需</Checkbox></Form.Item>
                    <Button size="small" disabled={taskIndex === 0} onClick={() => taskActions.move(taskIndex, taskIndex - 1)}>↑</Button>
                    <Button size="small" danger onClick={() => taskActions.remove(taskField.name)}>移除</Button></>}</div>
                </div>;
              })}
              <Form.Item noStyle shouldUpdate>{() => {
                const contentType = (form.getFieldValue('contentType') ?? 'VIDEO') as 'VIDEO' | 'ARTICLE';
                const current: FormTask[] = form.getFieldValue(['variants', variantField.name, 'tasks']) ?? [];
                const nextKey = nextCanonicalTaskKey(contentType, current.map((task) => task.taskKey));
                return <Button type="dashed" disabled={!nextKey} onClick={() => {
                  if (!nextKey) return; const publicationIndex = current.findIndex((task) => task.taskKey === 'PUBLICATION');
                  taskActions.add({ taskKey: nextKey, name: '', description: '', unit: '次', quantity: 1,
                    required: true, rate: '0.00' }, publicationIndex < 0 ? current.length : publicationIndex); }}>
                  <Icon name="plus" size={16} strokeWidth={2} /> 添加任务</Button>;
              }}</Form.Item>
            </div>}</Form.List>
          </section>)}
        </div>}</Form.List>
        <div className="service-template-form-footer"><span>保存后仍是草稿，发布前可继续修改。</span>
          <Space><Button onClick={() => setFormOpen(false)}>取消</Button><Button type="primary" htmlType="submit"
            loading={createMutation.isPending || updateMutation.isPending}>保存草稿</Button></Space></div>
      </Form>
    </Modal>

    <Modal title={`复制 ${cloneSource?.familyCode ?? ''} ${cloneSource ? `V${cloneSource.versionNo}` : ''}`}
      open={!!cloneSource} okText="复制为新草稿" onOk={() => void cloneVersion()}
      okButtonProps={{ disabled: !cloneDate, loading: cloneMutation.isPending }}
      onCancel={() => { setCloneSource(null); cloneAttempt.current = null; }}>
      <p>复制会在同一模板系列中建立下一版本，原版本及已有服务包快照保持不变。</p>
      <label className="service-template-date-field">新版本生效日期
        <DateInput value={cloneDate} onChange={(event) => setCloneDate(event.target.value)} /></label>
    </Modal>

    <Modal title="发布服务模板版本" open={publishOpen} okText="确认发布" onOk={() => void publishVersion()}
      okButtonProps={{ disabled: !preview.data, loading: publishMutation.isPending }}
      onCancel={() => { setPublishOpen(false); publishAttempt.current = null; }}>
      {preview.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
      {preview.isError && <Alert type="error" title="发布影响预览失败，暂不可发布" description={errorMessage(preview.error)} />}
      {preview.data && <><Alert type="info" showIcon title="发布只影响未来新建的服务包"
        description={`本次共识别 ${preview.data.changes.length} 项变更；既有服务包自动改变 0 个。`} />
        <div className="service-template-diff">{preview.data.changes.slice(0, 12).map((change) => <p key={change.field}>
          <strong>{change.field}</strong><span>{change.before ?? '未设置'} → {change.after ?? '已移除'}</span></p>)}
          {preview.data.changes.length > 12 && <small>另有 {preview.data.changes.length - 12} 项变更</small>}</div></>}
    </Modal>
  </main>;
}

function TemplateDetail({ detail, archived, canManage, canPublish, onEdit, onClone, onPublish }: {
  detail: ServiceTemplateVersionDetail; archived: boolean; canManage: boolean; canPublish: boolean;
  onEdit: () => void; onClone: () => void; onPublish: () => void;
}) {
  return <>
    <header className="service-template-detail-head"><div><span>{detail.familyCode} · V{detail.versionNo}</span>
      <h2>{detail.familyName}</h2><p>{detail.versionName} · {detail.contentType === 'VIDEO' ? '视频' : '文章'} · {detail.effectiveOn} 生效</p></div>
      <Space wrap>{!archived && detail.status === 'DRAFT' && canManage && <Button onClick={onEdit}>编辑草稿</Button>}
        {!archived && detail.status !== 'DRAFT' && canManage && <Button type="primary" onClick={onClone}>新建修订版本</Button>}
        {!archived && detail.status === 'DRAFT' && canPublish && <Button type="primary" onClick={onPublish}>发布版本</Button>}</Space>
    </header>
    {archived && <Alert className="workspace-alert" type="warning" showIcon title="该模板族已归档"
      description="归档模板仅供历史追溯，不能用于新建服务包，也不能编辑或发布。" />}
    {detail.status !== 'DRAFT' && <Alert className="workspace-alert" type="info" showIcon
      title="已发布版本不可直接修改"
      description="如需调整任务费用或发布预算，请新建修订版本；既有服务包继续使用原版本快照，不会被改写。" />}
    <div className="service-template-meta"><div><span>状态</span><strong>{statusLabel(detail.status)}</strong></div>
      <div><span>参考容量</span><strong>{detail.defaultCapacity} 条</strong></div>
      <div><span>动态规格</span><strong>{detail.variantCount}</strong></div>
      <div><span>生产任务</span><strong>{detail.productionTaskCount}</strong></div></div>
    <section className="service-template-deliverable"><span>交付内容与口径</span><p>{detail.deliverable}</p></section>
    <div className="service-variant-list">{detail.variants.map((variant, index) => <article key={variant.variantId}>
      <header><div><span>规格 {String(index + 1).padStart(2, '0')} · {variant.variantKey}</span>
        <h3>{variant.name}</h3><p>{variant.description}</p>
        {(variant.groupName || variant.rateLabel) && <p className="service-variant-tags">
          {variant.groupName && <Tag>{variant.groupName}</Tag>}{variant.rateLabel && <Tag>{variant.rateLabel}</Tag>}</p>}</div>
        <div className="service-variant-budget">
          <span>单条预算</span><strong>{money(variant.totalBudget)}</strong>
          <small>生产 {money(variant.productionBudget)} · 发布 {money(variant.publicationBudget)}</small></div></header>
      <div className="service-task-table"><div className="service-task-table-head"><span>任务</span><span>说明</span>
        <span>计价</span><span>单项费用</span></div>
        {variant.tasks.map((task) => <div key={task.ruleId} className={task.isSystemPublication ? 'is-publication' : ''}>
          <span><b>{task.name}</b>{task.isSystemPublication && <Tag variant="filled">固定</Tag>}
            {!task.required && <small>可选</small>}</span><span>{task.description}</span>
          <span>{task.quantity} {task.unit}</span><strong>{money(task.rate)}</strong></div>)}</div>
    </article>)}</div>
  </>;
}
